import React, { useCallback, useEffect, useState } from 'react';
import { Search, Check, X, Clock, FileText, Save } from 'lucide-react';
import Skeleton from '../common/Skeleton';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmDelete } from '../common/confirm';
import { capaFeedbackService, CapaFeedbackItem } from '../../services/capaFeedbackService';
import extractApiError from '../../utils/apiError';
import NoDataFound from '../common/NoDataFound';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedTextarea } from '../../validation/ValidatedFields';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

const FeedbackTab: React.FC = () => {
    const { formatDateTime } = useEntitySettings();
    const [rows, setRows] = useState<CapaFeedbackItem[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const [rejectFor, setRejectFor] = useState<CapaFeedbackItem | null>(null);
    const [rejectComment, setRejectComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const fieldVh = useFieldValidation();
    const { allowed: canModify, loading: permLoading } = usePermission('CAP_FEEDBACK', 'view+modify');
    const { allowed: canView, loading: viewLoading } = usePermission('CAP_FEEDBACK', 'view');

    const load = useCallback(async () => {
        if (!canView) return;
        try {
            setLoading(true);
            const { data, total } = await capaFeedbackService.list({
                q: search || undefined,
                limit: pageSize,
                offset: (page - 1) * pageSize,
            });
            setRows(data);
            setTotalRecords(total);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load feedback') });
        } finally { setLoading(false); }
    }, [search, page, pageSize, canView]);

    useEffect(() => { setPage(1); }, [search, pageSize]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!rejectFor) return;
        fieldVh.reset();
    }, [rejectFor, fieldVh]);

    const handleApprove = async (item: CapaFeedbackItem) => {
        if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
        const ok = await confirmDelete({
            title: `Approve "${item.name}"?`,
            text: 'Task will be marked VERIFIED. This is final.',
        });
        if (!ok) return;
        try {
            await capaFeedbackService.approve(item.id);
            setToast({ type: 'success', message: 'Approved.' });
            load();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to approve') });
        }
    };

    const handleRejectSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!rejectFor) return;
        if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
        const valid = fieldVh.validateAll([
            { name: 'comment', code: 110, value: rejectComment, required: true }
        ]);
        if (!valid) return;
        try {
            setSubmitting(true);
            await capaFeedbackService.reject(rejectFor.id, rejectComment.trim());
            setToast({ type: 'success', message: 'Rejected — task returned to doer.' });
            setRejectFor(null);
            setRejectComment('');
            load();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to reject') });
        } finally { setSubmitting(false); }
    };

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to CAPA feedback." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <span className="status-badge xs warning">
                        <Clock size={12} /> {totalRecords} pending
                    </span>
                    <FormControl.Input
                        type="text"
                        placeholder="Search feedback…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_capa_feedback_search"
                    />
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Task / Issue</th>
                                        <th className="table-list-th">Doer</th>
                                        <th className="table-list-th">Submitted</th>
                                        <th className="table-list-th">Approver / Validator</th>
                                        <th className="table-list-th">Doer Comment</th>
                                        <th className="table-list-th table-list-th--right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['3xs', 'md', 'sm', 'sm', 'sm', 'md', { width: 'icon', align: 'right' }]} />}
                                    {!loading && rows.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="table-list-cell">
                                                <NoDataFound
                                                    title={search ? 'No Matching Feedback' : 'No Pending Feedback'}
                                                    message={search ? 'No feedback items match your search.' : 'No tasks are awaiting feedback.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && rows.map((item, idx) => (
                                        <tr key={item.id} className="table-list-row">
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell">
                                                <span className="table-list-cell-title">{item.name}</span>
                                                {item.issue_code && (
                                                    <span className="table-list-cell-sub">{item.issue_code} — {item.issue_name}</span>
                                                )}
                                            </td>
                                            <td className="table-list-cell">{item.doer_name || `user#${item.doer_id}`}</td>
                                            <td className="table-list-cell">
                                                {item.submitted_on
                                                    ? formatDateTime(item.submitted_on)
                                                    : '—'}
                                            </td>
                                            <td className="table-list-cell">
                                                <div className="flex flex-col" style={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                                                    <span style={{ color: '#374151' }}>
                                                        <span style={{ color: '#9ca3af' }}>A: </span>
                                                        {item.approver_name?.trim() || (item.approver_id ? `user#${item.approver_id}` : '—')}
                                                    </span>
                                                    <span style={{ color: '#374151' }}>
                                                        <span style={{ color: '#9ca3af' }}>V: </span>
                                                        {item.validator_name?.trim() || (item.validator_id ? `user#${item.validator_id}` : '—')}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="table-list-cell" style={{ maxWidth: 320 }}>
                                                {item.doer_comment ? (
                                                    <span style={{ fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'pre-wrap' }} title={item.doer_comment}>
                                                        <FileText size={11} style={{ display: 'inline', marginRight: 4 }} />
                                                        {item.doer_comment.length > 120 ? item.doer_comment.slice(0, 120) + '…' : item.doer_comment}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">(no comment)</span>
                                                )}
                                            </td>
                                            <td className="table-list-cell table-list-cell--actions">
                                                <div className="flex items-center gap-1 justify-end">
                                                    <Button variant="fill" icon={Check} onClick={() => handleApprove(item)} disabled={permLoading || !canModify} title={!canModify ? NOT_AUTHORISED_MSG : undefined} data-testid={`BTN_capa_feedback_row_${item.id}_approve`}>Approve</Button>
                                                    <Button variant="outline" icon={X} onClick={() => { setRejectFor(item); setRejectComment(''); }} disabled={permLoading || !canModify} title={!canModify ? NOT_AUTHORISED_MSG : undefined} data-testid={`BTN_capa_feedback_row_${item.id}_reject`}>Reject</Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={[10, 25, 50, 100]}
                            recordLabel="items"
                        />
                    </div>
                </div>
            </div>

            <Modal isOpen={!!rejectFor} onClose={() => setRejectFor(null)} size="md">
                <Modal.Header>
                    Reject "<span className="item-highlight">{rejectFor?.name}</span>"
                </Modal.Header>
                <Modal.Body>
                    <p className="text-sm text-gray-600 mb-3">
                        Comment is required. Task status will flip to <strong>IN_PROGRESS</strong> so the doer can rework and resubmit.
                    </p>
                    <form id="reject-form" onSubmit={handleRejectSubmit} data-testid="MOD_capa_feedback_reject_form">
                        <ValidatedTextarea
                            vh={fieldVh}
                            name="comment"
                            code={110}
                            required
                            label="Rejection Comment"
                            value={rejectComment}
                            onChange={e => setRejectComment(e.target.value)}
                            placeholder="What needs to be fixed?"
                            rows={4}
                            data-testid="INP_capa_feedback_reject_comment"
                        />
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit" form="reject-form" variant="fill" icon={Save} disabled={permLoading || !canModify || submitting} title={!canModify ? NOT_AUTHORISED_MSG : undefined} data-testid="BTN_capa_feedback_reject_submit">
                        {submitting ? 'Rejecting...' : 'Reject'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default FeedbackTab;
